import { QueryResult, gql, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import contentProviderClient from '../client/medq-content-provider';
import { DifficultyName } from '../services/contents/medq-content-provider.client';
import { AsyncOperation, Question, QuestionDifficulty } from './@types';

export interface InstitutionSubjectsInfo {
  subject: string;
  ancestors: string[];
  questionCount: number;
  questions: Question[];
  difficulty: number;
}

interface GetInstitutionSubjectsInfoInput {
  subjectDepth: number;
  institutionName: string;
}

interface GetInstitutionSubjectsInfoOutput {
  subjectsInfo: InstitutionSubjectsInfo[];
}

const getQuestionDifficultyFromDifficultyName = (value: DifficultyName): QuestionDifficulty => {
  switch (value) {
    case 'easy':
      return QuestionDifficulty.EASY;
    case 'hard':
      return QuestionDifficulty.HARD;
    case 'medium':
      return QuestionDifficulty.MEDIUM;
    case 'unknown':
    default:
      return QuestionDifficulty.UNKNOWN;
  }
};

export function useGetQuestionsGroupedBySubject({
  institutionName,
  subjectDepth,
}: GetInstitutionSubjectsInfoInput): AsyncOperation<GetInstitutionSubjectsInfoOutput> {
  console.log('Executing query', { institutionName });
  const [result, setResult] = useState<AsyncOperation<GetInstitutionSubjectsInfoOutput>>({
    isLoading: true,
    data: undefined,
  });
  useEffect(() => {
    contentProviderClient.getQuestionsGroupedBySubject(institutionName, subjectDepth).then((res) => {
      setResult({
        isLoading: false,
        data: {
          subjectsInfo: res.questions.map((it) => ({
            subject: it.subjectId,
            ancestors: it.ancestors,
            difficulty: it.difficulty,
            questionCount: it.questionCount,
            questions: it.questions.map((question): Question => ({
              answers: question.answers.map((answer) => answer.body),
              body: question.body,
              correctAnswer: question.answers.findIndex((e) => e.isCorrect),
              difficulty: getQuestionDifficultyFromDifficultyName(question.difficultyName),
              id: question.questionId,
              institutionName: question.institutionName,
              resumeUrl: '',
              subject: question.subjectId ?? '',
              subjectNames: question.subjectAccumulatedNames ?? [],
              year: question.examYear,
              answersProportion: question.answers.map((answer, index) => ({
                answerIndex: index,
                count: answer.answeredCount,
              })),
            })),
          })),
        },
      });
    });
  }, [institutionName, subjectDepth]);
  return result;
}

interface QuestionStyle {
  style: string;
  count: number;
}

interface ListMainQuestionStylesFromInput {
  institutionName: string;
}

interface ListMainQuestionStylesFromOutput {
  styles: QuestionStyle[];
}

const LIST_MAIN_QUESTION_STYLES_FROM = gql`
  query ListMainQuestionStylesFrom(
    $institutionName: String!
  ) {
    styles: listMainQuestionStylesFrom(
      institutionName: $institutionName
    ) {
      style
      count
    }
  }
`;

export function useListMainQuestionStylesFrom(variables: ListMainQuestionStylesFromInput): QueryResult<
ListMainQuestionStylesFromOutput, ListMainQuestionStylesFromInput
> {
  return useQuery<ListMainQuestionStylesFromOutput, ListMainQuestionStylesFromInput>(LIST_MAIN_QUESTION_STYLES_FROM, {
    variables,
    skip: !variables.institutionName,
  });
}
